import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { MapPin, MessageCircle, Mail, PhoneCall, Linkedin, ExternalLink } from 'lucide-react';
import { socialLinks, contactInfo, contactByLanguage } from '../data/mock';

// Google Colors
const googleColors = {
  blue: '#4285F4',
  red: '#EA4335',
  yellow: '#FBBC04',
  green: '#34A853',
};

const Footer = () => {
  const { language, t } = useLanguage();

  // Get contact info based on current language
  const currentContact = contactByLanguage[language] || contactByLanguage.es;

  // URL paths by language
  const urlPaths = {
    es: { services: 'servicios', webCreation: 'creacion-web', contact: 'contacto', privacy: 'privacidad' },
    fr: { services: 'services', webCreation: 'creation-web', contact: 'contact', privacy: 'confidentialite' },
    en: { services: 'services', webCreation: 'web-creation', contact: 'contact', privacy: 'privacy' },
  };

  const paths = urlPaths[language] || urlPaths.es;

  const openWhatsApp = () => {
    window.open(`https://wa.me/${currentContact.whatsapp}`, '_blank');
  };

  const callPhone = () => {
    window.location.href = `tel:${currentContact.phone.replace(/\s/g, '')}`;
  };

  return (
    <footer className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-2 mb-4">
              <div className="flex space-x-1">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: googleColors.blue }} />
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: googleColors.red }} />
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: googleColors.yellow }} />
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: googleColors.green }} />
              </div>
              <span className="text-xl font-bold">Agencia <span style={{ color: googleColors.blue }}>PPC</span></span>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed max-w-md mb-6">
              {t.footer.description}
            </p>
            <div className="flex items-center space-x-2 text-slate-400 mb-4">
              <MapPin className="w-4 h-4" style={{ color: googleColors.red }} />
              <span className="text-sm">{t.footer.location}</span>
            </div>
            {/* LinkedIn */}
            <a
              href={socialLinks.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-slate-400 hover:text-[#0A66C2] text-sm transition-colors"
            >
              <Linkedin className="w-4 h-4 mr-2" />
              LinkedIn
              <ExternalLink className="w-3 h-3 ml-1" />
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-300 mb-4">Links</h4>
            <ul className="space-y-3">
              <li>
                <Link to={`/${language}`} className="text-slate-400 hover:text-white text-sm transition-colors duration-200">
                  {t.nav.home}
                </Link>
              </li>
              <li>
                <Link to={`/${language}/${paths.services}`} className="text-slate-400 hover:text-white text-sm transition-colors duration-200">
                  {t.nav.services}
                </Link>
              </li>
              <li>
                <Link to={`/${language}/compliance`} className="text-slate-400 hover:text-white text-sm transition-colors duration-200">
                  {t.nav.compliance}
                </Link>
              </li>
              <li>
                <Link to={`/${language}/${paths.webCreation}`} className="text-slate-400 hover:text-white text-sm transition-colors duration-200">
                  {t.nav.webCreation}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-300 mb-4">Contact</h4>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={callPhone}
                  className="flex items-center space-x-2 text-slate-400 hover:text-white text-sm transition-colors duration-200"
                >
                  <PhoneCall className="w-4 h-4" style={{ color: googleColors.green }} />
                  <span>{currentContact.phone}</span>
                </button>
              </li>
              <li>
                <button
                  onClick={openWhatsApp}
                  className="flex items-center space-x-2 text-slate-400 hover:text-emerald-400 text-sm transition-colors duration-200"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>WhatsApp</span>
                </button>
              </li>
              <li>
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="flex items-center space-x-2 text-slate-400 hover:text-white text-sm transition-colors duration-200"
                >
                  <Mail className="w-4 h-4" style={{ color: googleColors.blue }} />
                  <span>{contactInfo.email}</span>
                </a>
              </li>
              <li>
                <div className="flex items-center space-x-2 text-slate-400 text-sm">
                  <MapPin className="w-4 h-4" style={{ color: googleColors.red }} />
                  <span>{currentContact.location}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-slate-800 mt-12 pt-8">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <p className="text-slate-500 text-sm">{t.footer.copyright}</p>
            <div className="flex items-center space-x-6">
              <Link to={`/${language}/legal`} className="text-slate-500 hover:text-white text-sm transition-colors duration-200">
                {t.footer.legal}
              </Link>
              <Link to={`/${language}/${paths.privacy}`} className="text-slate-500 hover:text-white text-sm transition-colors duration-200">
                {t.footer.privacy}
              </Link>
              <Link to={`/${language}/cookies`} className="text-slate-500 hover:text-white text-sm transition-colors duration-200">
                {t.footer.cookies}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
